import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { API_BASE_URL } from './api-base';

@Injectable({
  providedIn: 'root'
})
export class ControlaiService {
  private apiUrl = `${API_BASE_URL}/controlai`;

  constructor(private http: HttpClient) {}

  listCommands(tenantId: number, search = '', active: number | null = null): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    if (active !== null && active !== undefined) {
      params = params.set('active', String(Number(active) ? 1 : 0));
    }
    return this.http.get<any[]>(`${this.apiUrl}/commands`, { params });
  }

  createCommand(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/commands`, payload);
  }

  updateCommand(commandId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/commands/${commandId}`, payload);
  }

  uploadCommandImages(payload: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/commands/uploads/images`, payload);
  }

  getCommandHistory(commandId: number, tenantId: number): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.apiUrl}/commands/${commandId}/history`, { params });
  }

  getCommandDashboard(commandId: number, tenantId: number): Observable<any> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any>(`${this.apiUrl}/commands/${commandId}/dashboard`, { params });
  }

  getCommandScopeOptions(tenantId: number): Observable<{ sectors: any[]; departments: any[]; users: any[] }> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<{ sectors: any[]; departments: any[]; users: any[] }>(`${this.apiUrl}/commands/scope-options`, {
      params
    });
  }

  listTransactions(
    tenantId: number,
    filters?: {
      command_id?: number | null;
      status?: string | null;
      lifecycle_status?: string | null;
      validation_result?: string | null;
      reference_id?: string | null;
    }
  ): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (filters?.command_id) {
      params = params.set('command_id', String(filters.command_id));
    }
    if (filters?.status) {
      params = params.set('status', String(filters.status));
    }
    if (filters?.lifecycle_status) {
      params = params.set('lifecycle_status', String(filters.lifecycle_status));
    }
    if (filters?.validation_result) {
      params = params.set('validation_result', String(filters.validation_result));
    }
    if (filters?.reference_id?.trim()) {
      params = params.set('reference_id', filters.reference_id.trim());
    }
    return this.http.get<any[]>(`${this.apiUrl}/transactions`, { params });
  }

  createTransaction(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transactions`, payload);
  }

  startTransaction(payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transactions/start`, payload);
  }

  saveTransactionScope(transactionId: number, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/transactions/${transactionId}/scope`, payload);
  }

  uploadTransactionImages(payload: FormData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transactions/uploads/images`, payload);
  }

  executeTransaction(transactionId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transactions/${transactionId}/execute`, payload);
  }

  deleteTransaction(transactionId: number, payload: any): Observable<any> {
    const endpoint = `${this.apiUrl}/transactions/${transactionId}`;
    return this.http.request<any>('delete', endpoint, { body: payload }).pipe(
      catchError((error) => {
        const status = Number(error?.status || 0);
        if (status === 404 || status === 405 || status === 501) {
          return this.http.post<any>(`${endpoint}/delete`, payload);
        }
        return throwError(() => error);
      })
    );
  }

  getTransactionHistory(transactionId: number, tenantId: number): Observable<any[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.apiUrl}/transactions/${transactionId}/history`, { params });
  }

  interactTransaction(transactionId: number, payload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/transactions/${transactionId}/interact`, payload);
  }

  updateTransactionLifecycle(transactionId: number, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/transactions/${transactionId}/lifecycle`, payload);
  }

  updateValidation(transactionId: number, payload: any): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/transactions/${transactionId}/validation`, payload);
  }

  listHistory(tenantId: number, entityType?: 'COMMAND' | 'TRANSACTION', entityId?: number): Observable<any[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (entityType) {
      params = params.set('entity_type', entityType);
    }
    if (entityId) {
      params = params.set('entity_id', entityId);
    }
    return this.http.get<any[]>(`${this.apiUrl}/history`, { params });
  }
}
